import { RESOURCE_DOCUMENT_TYPES } from './resourceDocumentConstants';

export type ResourceDocumentCopy = {
  shortLabel: string;
  formTitle: string;
  formSubtitle: (documentName: string) => string;
  gettingResource: string;
  getResource: string;
  readySubtitle: string;
  downloadLabel: string;
  emailLabel: string;
  sendToEmail: string;
  changeEmailSubtitle: string;
  deliverTitle: string;
  deliverBody: string;
  emailSuccessTitle: string;
  emailErrorTitle: string;
  emailSentTo: (email: string) => string;
  emailFailedBody: string;
  emailFailedFallback: string;
  deliveredTo: string;
  previewUnavailable: string;
  unavailableMessage: string;
};

const DEFAULT_COPY: ResourceDocumentCopy = {
  shortLabel: 'resource',
  formTitle: 'Download Resource',
  formSubtitle: (name) =>
    `Please enter your details below to access ${name || 'this resource'}.`,
  gettingResource: 'GETTING RESOURCE...',
  getResource: 'GET RESOURCE',
  readySubtitle: 'YOUR RESOURCE IS READY.',
  downloadLabel: 'DOWNLOAD',
  emailLabel: 'EMAIL',
  sendToEmail: 'SEND EMAIL',
  changeEmailSubtitle: "Update where we'll deliver your resource.",
  deliverTitle: 'Deliver Your Resource',
  deliverBody: "We'll send this document to:",
  emailSuccessTitle: 'Resource Sent Successfully',
  emailErrorTitle: "We Couldn't Deliver Your Resource",
  emailSentTo: (email) => `We've sent the document to ${email}.`,
  emailFailedBody: "We couldn't send the email right now.",
  emailFailedFallback: 'Please download it directly instead.',
  deliveredTo: "We've delivered your resource to:",
  previewUnavailable: 'Preview unavailable. Please download the document.',
  unavailableMessage:
    'This resource is not available right now. Please try again later or contact support.',
};

const WHITE_PAPER_COPY: ResourceDocumentCopy = {
  shortLabel: 'white paper',
  formTitle: 'Download White Paper',
  formSubtitle: (name) =>
    `Please enter your details below to access ${name || 'the white paper'}.`,
  gettingResource: 'GETTING WHITE PAPER...',
  getResource: 'GET WHITE PAPER',
  readySubtitle: 'YOUR WHITE PAPER IS READY.',
  downloadLabel: 'DOWNLOAD',
  emailLabel: 'EMAIL',
  sendToEmail: 'SEND EMAIL',
  changeEmailSubtitle: "Update where we'll deliver your white paper.",
  deliverTitle: 'Deliver Your White Paper',
  deliverBody: "We'll send this document to:",
  emailSuccessTitle: 'White Paper Sent Successfully',
  emailErrorTitle: "We Couldn't Deliver Your White Paper",
  emailSentTo: (email) => `We've sent the document to ${email}.`,
  emailFailedBody: "We couldn't send the email right now.",
  emailFailedFallback: 'Please download it directly instead.',
  deliveredTo: "We've delivered your white paper to:",
  previewUnavailable: 'Preview unavailable. Please download the document.',
  unavailableMessage:
    'White paper download is not available right now. Please try again later or contact support.',
};

const EXECUTIVE_SUMMARY_COPY: ResourceDocumentCopy = {
  shortLabel: 'executive summary',
  formTitle: 'Download Executive Summary',
  formSubtitle: (name) =>
    `Please enter your details below to access ${name || 'the executive summary'}.`,
  gettingResource: 'GETTING EXECUTIVE SUMMARY...',
  getResource: 'GET EXECUTIVE SUMMARY',
  readySubtitle: 'YOUR EXECUTIVE SUMMARY IS READY.',
  downloadLabel: 'DOWNLOAD',
  emailLabel: 'EMAIL',
  sendToEmail: 'SEND EMAIL',
  changeEmailSubtitle: "Update where we'll deliver your executive summary.",
  deliverTitle: 'Deliver Your Executive Summary',
  deliverBody: "We'll send this document to:",
  emailSuccessTitle: 'Executive Summary Sent Successfully',
  emailErrorTitle: "We Couldn't Deliver Your Executive Summary",
  emailSentTo: (email) => `We've sent the document to ${email}.`,
  emailFailedBody: "We couldn't send the email right now.",
  emailFailedFallback: 'Please download it directly instead.',
  deliveredTo: "We've delivered your executive summary to:",
  previewUnavailable: 'Preview unavailable. Please download the document.',
  unavailableMessage:
    'Executive summary download is not available right now. Please try again later or contact support.',
};

const BROCHURE_COPY: ResourceDocumentCopy = {
  shortLabel: 'brochure',
  formTitle: 'Download Brochure',
  formSubtitle: (name) =>
    `Please enter your details below to access ${name || 'the brochure'}.`,
  gettingResource: 'GETTING BROCHURE...',
  getResource: 'GET BROCHURE',
  readySubtitle: 'YOUR BROCHURE IS READY.',
  downloadLabel: 'DOWNLOAD',
  emailLabel: 'EMAIL',
  sendToEmail: 'SEND EMAIL',
  changeEmailSubtitle: "Update where we'll deliver your brochure.",
  deliverTitle: 'Deliver Your Brochure',
  deliverBody: "We'll send this document to:",
  emailSuccessTitle: 'Brochure Sent Successfully',
  emailErrorTitle: "We Couldn't Deliver Your Brochure",
  emailSentTo: (email) => `We've sent the document to ${email}.`,
  emailFailedBody: "We couldn't send the email right now.",
  emailFailedFallback: 'Please download it directly instead.',
  deliveredTo: "We've delivered your brochure to:",
  previewUnavailable: 'Preview unavailable. Please download the document.',
  unavailableMessage:
    'Brochure download is not available right now. Please try again later or contact support.',
};

export function getResourceDocumentCopy(documentType: string | undefined): ResourceDocumentCopy {
  const normalized = documentType?.trim();

  switch (normalized) {
    case RESOURCE_DOCUMENT_TYPES.WHITE_PAPER:
      return WHITE_PAPER_COPY;
    case RESOURCE_DOCUMENT_TYPES.EXECUTIVE_SUMMARY:
    case 'One Pager':
      return EXECUTIVE_SUMMARY_COPY;
    case RESOURCE_DOCUMENT_TYPES.BROCHURE:
      return BROCHURE_COPY;
    default:
      return DEFAULT_COPY;
  }
}
